import type { SubcategoryListingRule } from "@/lib/subcategoryRules";
import {
  getSellListingPerItemFieldErrors,
  sellListingPerItemFieldsValid,
  validateSellListingField,
  validatePriceAgainstBand,
  type SellListingFormErrors,
  type SellListingPriceBand,
} from "@/lib/listing/sellListingValidation";
import {
  BULK_LISTING_MIN_ITEMS,
  BULK_LISTING_MIN_ITEMS_MESSAGE,
  type BulkListingCommonState,
  type BulkListingItemDraft,
} from "@/lib/listing/bulkListingTypes";
import { emptyFamilySpecs } from "@/lib/listing/listingFamilyRuntime";
import type { ListingSpecWave } from "@/lib/listing/specRequirements";
import { getListingSpecFormValidationMessage } from "@/lib/listing/listingSpecPlugin";

export interface BulkCommonErrors {
  location?: string;
}

export function validateBulkCommon(
  common: BulkListingCommonState,
): BulkCommonErrors {
  return {
    location: validateSellListingField("location", common.location),
  };
}

export function bulkCommonIsValid(errors: BulkCommonErrors): boolean {
  return !errors.location;
}

export function bulkBatchMeetsMinimumItems(
  items: BulkListingItemDraft[],
): boolean {
  return items.length >= BULK_LISTING_MIN_ITEMS;
}

export function getBulkBatchMinimumItemError(
  items: BulkListingItemDraft[],
): string | undefined {
  return bulkBatchMeetsMinimumItems(items)
    ? undefined
    : BULK_LISTING_MIN_ITEMS_MESSAGE;
}

export type BulkItemBandValidationOptions = {
  band?: SellListingPriceBand | null;
  enforcementActive?: boolean;
};

export function getBulkItemFieldErrors(
  draft: BulkListingItemDraft,
  bandOptions?: BulkItemBandValidationOptions,
): Pick<
  SellListingFormErrors,
  "title" | "price" | "category" | "images" | "description" | "defects"
> {
  const errors = getSellListingPerItemFieldErrors(
    {
      title: draft.title,
      price: draft.price,
      category: draft.category,
      brand: draft.brand,
    },
    draft.images,
  );
  const bandPriceError = validatePriceAgainstBand(
    draft.price,
    bandOptions?.band,
    {
      enforcementActive: bandOptions?.enforcementActive,
    },
  );
  if (bandPriceError) {
    errors.price = bandPriceError;
  }
  return {
    ...errors,
    description: validateSellListingField("description", draft.description),
    defects: validateSellListingField("defects", draft.defects),
  };
}

export function bulkItemRulesSatisfied(
  draft: BulkListingItemDraft,
  subcategoryRule: SubcategoryListingRule | null,
): boolean {
  if (!subcategoryRule) return true;
  return draft.rulesAcknowledged;
}

export function bulkItemIsComplete(
  draft: BulkListingItemDraft,
  subcategoryRule: SubcategoryListingRule | null,
  activeSubcategories?: Iterable<string>,
  enabledWaves?: readonly ListingSpecWave[],
  bandOptions?: BulkItemBandValidationOptions,
): boolean {
  const fieldErrors = getBulkItemFieldErrors(draft, bandOptions);
  const specError = getBulkItemSpecError(
    draft,
    activeSubcategories,
    enabledWaves,
  );
  return (
    sellListingPerItemFieldsValid(fieldErrors) &&
    bulkItemRulesSatisfied(draft, subcategoryRule) &&
    !specError
  );
}

export function getBulkItemSpecError(
  draft: BulkListingItemDraft,
  activeSubcategories?: Iterable<string>,
  enabledWaves?: readonly ListingSpecWave[],
): string | undefined {
  if (!draft.subcategory) return undefined;
  if (!activeSubcategories) return undefined;
  return getListingSpecFormValidationMessage({
    subcategoryId: draft.subcategory,
    sizeText: draft.size,
    familySpecs: draft.familySpecs ?? emptyFamilySpecs(),
    activeSubcategoryIds: activeSubcategories,
    enabledWaves,
  });
}

export function validateFullBulkBatch(
  common: BulkListingCommonState,
  items: BulkListingItemDraft[],
  getRuleForSubcategory: (
    subcategoryId: string,
  ) => SubcategoryListingRule | null,
  activeSubcategories?: Iterable<string>,
  enabledWaves?: readonly ListingSpecWave[],
): {
  commonErrors: BulkCommonErrors;
  perItemErrors: SellListingFormErrors[];
  rulesOk: boolean[];
  specErrors: (string | undefined)[];
} {
  const commonErrors = validateBulkCommon(common);
  const perItemErrors: SellListingFormErrors[] = [];
  const rulesOk: boolean[] = [];
  const specErrors: (string | undefined)[] = [];
  for (const draft of items) {
    const rule = draft.subcategory
      ? getRuleForSubcategory(draft.subcategory)
      : null;
    perItemErrors.push(getBulkItemFieldErrors(draft));
    rulesOk.push(bulkItemRulesSatisfied(draft, rule));
    specErrors.push(
      getBulkItemSpecError(draft, activeSubcategories, enabledWaves),
    );
  }
  return { commonErrors, perItemErrors, rulesOk, specErrors };
}
